import type { HelpAgentApp } from '@/components/help-agent-app';
import { DEFAULT_ATTACHMENT_POLICY } from '@/constants/attachment-policy';
import { createHelpAgentMockBackend, isHelpAgentMockModeEnabled } from '@/testing/mock-backend';
import type { ChatConfig } from '@/types/chat';
import { resolveHelpCenterFooterLinks } from '@/utils/help-center-links';
import { generateSessionId } from '@/utils/ids';

const TOKEN_STORAGE_KEY = 'ai_helper_token';

function buildStandaloneConfig(sessionId: string): ChatConfig {
  return {
    sessionId,
    wsUrl: `/wapi/ai-helper/notifications/${sessionId}`,
    apiUrl: '/wapi/ai-helper',
  };
}

function persistStandaloneAuthToken(token: string | null): void {
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // Ignore storage access errors in restricted contexts.
  }
}

function loadStandaloneAuthToken(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const fromQuery = urlParams.get('token');

  if (fromQuery) {
    persistStandaloneAuthToken(fromQuery);
    return fromQuery;
  }

  try {
    const fromStorage = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (fromStorage) {
      return fromStorage;
    }
  } catch {
    // Ignore storage access errors in restricted contexts.
  }

  const cookieMatch = document.cookie.match(/(?:^|;\s*)ai_helper_token=([^;]+)/);
  return cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
}

function loadStandaloneChatConfig(): ChatConfig {
  if (window.CONFIG?.sessionId && window.CONFIG?.wsUrl && window.CONFIG?.apiUrl) {
    return window.CONFIG;
  }

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id') || generateSessionId();
  const config = buildStandaloneConfig(sessionId);
  window.CONFIG = config;
  return config;
}

function syncStandaloneLocation(config: ChatConfig, token: string | null): void {
  const params = new URLSearchParams(window.location.search);
  params.set('session_id', config.sessionId);

  if (token) {
    params.set('token', token);
  } else {
    params.delete('token');
  }

  const query = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  window.CONFIG = config;
  persistStandaloneAuthToken(token);
}

function createStandaloneSession(token: string | null): ChatConfig {
  const config = buildStandaloneConfig(generateSessionId());
  syncStandaloneLocation(config, token);
  return config;
}

export function bootstrapStandaloneHelpAgent(app: HelpAgentApp): void {
  const token = loadStandaloneAuthToken();
  const config = loadStandaloneChatConfig();
  const isMockMode = isHelpAgentMockModeEnabled();
  const mockBackend = isMockMode ? createHelpAgentMockBackend() : null;

  app.notificationsEnabled = true;
  app.attachmentPolicy = {
    ...DEFAULT_ATTACHMENT_POLICY,
    maxFiles: 10,
  };
  app.submitHandler = mockBackend?.submitHandler ?? null;
  app.streamHandler = mockBackend?.streamHandler ?? null;
  app.setRuntimeConfig(config);
  app.setAuthToken(token);
  app.footerLinks = resolveHelpCenterFooterLinks(window.location.hostname);
  app.focusInput();

  app.addEventListener('help-agent:new-chat', () => {
    const nextConfig = createStandaloneSession(app.token);
    app.setRuntimeConfig(nextConfig);
    app.notify('Started a new chat', 'info');
    app.focusInput();
  });
}
